import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Activity,
  Eye,
  Layers,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Flame,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  MessageCircle,
  Palette,
  Stethoscope,
  Users,
  X,
  Zap,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import QuickTodoSheet, { QuickTodoTrigger } from "@/components/QuickTodoSheet";
import NotificationBell from "@/components/NotificationBell";
import ScheduledCallReminder from "@/components/ScheduledCallReminder";
import LeadAssignmentNotifier from "@/components/LeadAssignmentNotifier";
import EnableAlertsButton from "@/components/EnableAlertsButton";
import { bootstrapNotifyPermission } from "@/lib/notify";
import InstallPwaButton from "@/components/InstallPwaButton";
import InstallPwaBanner from "@/components/InstallPwaBanner";
import WaDailyNudgeModal from "@/components/WaDailyNudgeModal";
import { useWhatsAppInbox } from "@/contexts/WhatsAppInboxContext";

const STORAGE_KEY = "medix_sidebar_collapsed";
const ACTIVITY_LAST_SEEN_KEY = "medix_activity_last_seen";
const ACTIVITY_POLL_MS = 60_000;

const NAV_ITEMS = [
  {
    to: "/admin",
    label: "Dashboard",
    icon: LayoutDashboard,
    end: true,
    role: "all",
    testid: "nav-dashboard",
  },
  {
    to: "/admin/leads",
    label: "Leads",
    icon: Users,
    role: "all",
    testid: "nav-leads",
  },
  {
    to: "/admin/counselings",
    label: "Counselings",
    icon: Flame,
    role: "all",
    testid: "nav-counselings",
  },
  {
    to: "/admin/whatsapp",
    label: "WhatsApp Inbox",
    icon: MessageCircle,
    role: "all",
    testid: "nav-whatsapp-inbox",
  },
  {
    to: "/admin/dialer",
    label: "Power Dialer",
    icon: Zap,
    role: "all",
    testid: "open-dialer",
    accent: true,
  },
  {
    to: "/admin/activity",
    label: "Meda Eye",
    icon: Eye,
    role: "all",
    testid: "nav-activity",
  },
  {
    to: "/admin/campaigns",
    label: "Campaigns",
    icon: Megaphone,
    role: "admin",
    testid: "open-campaigns",
  },
  {
    to: "/admin/universities",
    label: "Universities",
    icon: Stethoscope,
    role: "admin",
    testid: "nav-universities",
  },
  {
    to: "/admin/analytics",
    label: "Analytics",
    icon: BarChart3,
    role: "admin",
    testid: "open-analytics",
  },
  {
    to: "/admin/settings",
    label: "Settings",
    icon: Palette,
    role: "admin",
    testid: "open-settings",
  },
];

const ROLE_LABEL = {
  admin: "Admin",
  counsellor: "Counsellor",
  super_admin: "Super Admin",
};

export default function AdminShell() {
  const { user, logout } = useAuth();
  const { needsReply: waUnread } = useWhatsAppInbox();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user?.role === "admin";
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* noop */
    }
  }, [collapsed]);

  // Request native notification permission once (Option A)
  useEffect(() => {
    bootstrapNotifyPermission();
  }, []);


  // Close mobile drawer on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  // --- Activity Feed unread badge ---
  const [activityUnread, setActivityUnread] = useState(0);
  const isOnActivity = loc.pathname.startsWith("/admin/activity");

  // When user visits Activity Feed, mark "seen" and reset counter immediately.
  useEffect(() => {
    if (!isOnActivity) return;
    try {
      localStorage.setItem(ACTIVITY_LAST_SEEN_KEY, new Date().toISOString());
    } catch {
      /* noop */
    }
    setActivityUnread(0);
  }, [isOnActivity]);

  // Poll unread count every minute (skips while on the feed itself).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      if (isOnActivity) return;
      try {
        const since = localStorage.getItem(ACTIVITY_LAST_SEEN_KEY);
        const r = await api.get("/admin/activity-feed/count", {
          params: since ? { since } : {},
        });
        if (!cancelled) setActivityUnread(r?.data?.count || 0);
      } catch {
        /* silent */
      }
    };
    tick();
    const id = setInterval(tick, ACTIVITY_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, isOnActivity]);

  const [customPipelines, setCustomPipelines] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/pipelines");
        if (alive) setCustomPipelines(r.data?.pipelines || []);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  const items = [
    ...NAV_ITEMS.filter(
      (i) => i.role === "all" || (i.role === "admin" && isAdmin),
    ),
    ...customPipelines.map((p) => ({
      to: `/admin/pipeline/${p.id}`,
      label: p.name,
      icon: Layers,
      role: "all",
      testid: `nav-pipeline-${p.id}`,
    })),
  ];

  // --- Quick notes (personal todos) ---
  const [todoOpen, setTodoOpen] = useState(false);
  const [todoOpenCount, setTodoOpenCount] = useState(0);

  // Poll open count every 90s so the sidebar badge stays fresh
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/admin/personal-todos");
        if (!cancelled) {
          const items = r?.data?.items || [];
          const now = Date.now();
          const isSnoozed = (i) =>
            i.snoozed_until && new Date(i.snoozed_until).getTime() > now;
          // Sidebar badge counts only active todos (not done, not snoozed)
          setTodoOpenCount(items.filter((i) => !i.done && !isSnoozed(i)).length);
        }
      } catch {
        /* silent */
      }
    };
    tick();
    const id = setInterval(tick, 90_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, todoOpen]);

  return (
    <div className="meda-app flex min-h-screen bg-[#F0F2F5]">
      {/* Global notification bell — fixed top-right across all admin pages */}
      <NotificationBell />
      <EnableAlertsButton />
      <ScheduledCallReminder />
      <LeadAssignmentNotifier />
      <WaDailyNudgeModal />

      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={() => setMobileOpen((v) => !v)}
        className="fixed left-3 top-3 z-50 flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white shadow-sm md:hidden"
        data-testid="admin-mobile-menu"
        aria-label="Toggle navigation"
      >
        {mobileOpen ? (
          <X className="h-5 w-5 text-[#1877F2]" />
        ) : (
          <Menu className="h-5 w-5 text-[#1877F2]" />
        )}
      </button>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm md:hidden"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`
          fixed inset-y-0 left-0 z-40 flex h-screen flex-col border-r border-slate-200 bg-white
          transition-[width,transform] duration-200 ease-out
          md:sticky md:top-0 md:translate-x-0
          ${collapsed ? "w-[72px]" : "w-[240px]"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Brand */}
        <div
          className={`flex h-16 items-center border-b border-slate-200 ${
            collapsed ? "justify-center px-2" : "gap-3 px-5"
          }`}
        >
          <MedaEyeMark />
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-display truncate text-base font-bold tracking-tight text-[#0F172A]">
                Meda <span className="text-[#1877F2]">AI</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400">
                Growth OS
              </div>
            </div>
          )}
        </div>

        {/* Tenant chip */}
        {!collapsed && user?.tenant_name && (
          <div className="mx-3 mt-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">
              Consultancy
            </div>
            <div
              className="mt-0.5 truncate text-xs font-semibold text-[#0A2540]"
              title={user.tenant_name}
            >
              {user.tenant_name}
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-4">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `mb-1 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors
                ${collapsed ? "justify-center px-0" : ""}
                ${
                  isActive
                    ? "bg-[#E7F0FF] text-[#1877F2] font-semibold"
                    : item.accent
                      ? "text-[#1877F2] hover:bg-[#E7F0FF]"
                      : "text-slate-600 hover:bg-slate-100 hover:text-[#0F172A]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="relative shrink-0">
                    <item.icon
                      className={`h-[18px] w-[18px] ${
                        item.accent && !isActive ? "text-[#1877F2]" : ""
                      }`}
                    />
                    {item.to === "/admin/activity" && (
                      <span
                        data-testid="nav-activity-live"
                        className="absolute -right-1 -top-1 flex h-2 w-2"
                        aria-label="Live"
                      >
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                      </span>
                    )}
                    {item.to === "/admin/whatsapp" && waUnread > 0 && collapsed && (
                      <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#25D366] px-1 text-[9px] font-bold text-white">
                        {waUnread > 99 ? "99+" : waUnread}
                      </span>
                    )}
                  </div>
                  {!collapsed && (
                    <span className="flex flex-1 items-center justify-between truncate">
                      <span className="truncate">{item.label}</span>
                      {item.to === "/admin/activity" && activityUnread > 0 && (
                        <span
                          data-testid="nav-activity-badge"
                          className={`ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
                            isActive
                              ? "bg-white/15 text-white"
                              : "bg-rose-500 text-white"
                          }`}
                        >
                          {activityUnread > 99 ? "99+" : activityUnread}
                        </span>
                      )}
                      {item.to === "/admin/whatsapp" && waUnread > 0 && (
                        <span
                          data-testid="nav-whatsapp-badge"
                          className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#25D366] px-1.5 text-[10px] font-semibold text-white"
                        >
                          {waUnread > 99 ? "99+" : waUnread}
                        </span>
                      )}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}

          {/* Quick notes — opens a per-user todo Sheet */}
          <div className="mt-2 border-t border-slate-100 pt-2">
            <QuickTodoTrigger
              collapsed={collapsed}
              openCount={todoOpenCount}
              onClick={() => setTodoOpen(true)}
            />
          </div>
        </nav>

        {/* User block */}
        <div className="border-t border-slate-100 p-2">
          {!collapsed ? (
            <div className="px-3 py-2">
              <div
                className="truncate text-sm font-semibold text-[#0A2540]"
                title={user?.name}
              >
                {user?.name || "—"}
              </div>
              <div
                className="truncate text-[11px] text-slate-500"
                title={user?.email}
              >
                {user?.email}
              </div>
              <div className="mt-0.5 text-[9px] uppercase tracking-[0.2em] text-[#C9A961]">
                {ROLE_LABEL[user?.role] || user?.role}
              </div>
            </div>
          ) : (
            <div
              className="mx-auto my-1 flex h-8 w-8 items-center justify-center rounded-full bg-[#0A2540] text-[11px] font-semibold text-white"
              title={user?.name}
            >
              {(user?.name || "?").trim().charAt(0).toUpperCase()}
            </div>
          )}
          {!collapsed && (
            <div className="mb-1 px-1">
              <InstallPwaButton
                label="Install app"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-[#1877F2]/30 bg-[#1877F2]/5 px-3 py-2 text-xs font-medium text-[#1877F2] transition-colors hover:bg-[#1877F2]/10"
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              logout();
              nav("/admin/login");
            }}
            data-testid="admin-logout"
            title={collapsed ? "Logout" : undefined}
            className={`
              flex w-full items-center gap-3 rounded-md py-2 text-sm text-slate-600 transition-colors
              hover:bg-rose-50 hover:text-rose-700
              ${collapsed ? "justify-center px-0" : "px-3"}
            `}
          >
            <LogOut className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>

        {/* Collapse toggle (desktop only) */}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="admin-sidebar-toggle"
          className="absolute -right-3 top-20 hidden h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:text-[#0A2540] md:flex"
        >
          {collapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronLeft className="h-3 w-3" />
          )}
        </button>
      </aside>

      {/* Main content area */}
      <div className="flex min-w-0 flex-1 flex-col">
        <InstallPwaBanner />
        <Outlet />
      </div>

      {/* Quick notes sheet — global per-user scratchpad */}
      <QuickTodoSheet open={todoOpen} onOpenChange={setTodoOpen} />
    </div>
  );
}

/* Compact animated Meda eye logo for the sidebar brand */
function MedaEyeMark() {
  return (
    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center">
      <svg viewBox="0 0 64 64" className="h-9 w-9">
        <defs>
          <radialGradient id="navIris" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#9CC6FF" />
            <stop offset="45%" stopColor="#2F7DF6" />
            <stop offset="100%" stopColor="#0A1A3A" />
          </radialGradient>
          <linearGradient id="navGold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="55%" stopColor="#1877F2" />
            <stop offset="100%" stopColor="#1E3A8A" />
          </linearGradient>
        </defs>
        <path d="M6,32 Q32,8 58,32 Q32,56 6,32 Z" fill="none" stroke="url(#navGold)" strokeWidth="2.4" />
        <circle cx="32" cy="32" r="13" fill="url(#navIris)" />
        <circle cx="32" cy="32" r="14.5" fill="none" stroke="url(#navGold)" strokeWidth="1.2" opacity="0.8" />
        <circle cx="32" cy="32" r="5.5" fill="#040A18" />
        <circle cx="29" cy="29" r="1.8" fill="#fff" opacity="0.85" />
      </svg>
    </div>
  );
}
